import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(undefined);

export const ThemeProvider = ({ children }) => {
  // Initialize theme: dark by default, respect explicit user choice only
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('2a-theme');
      if (stored) return stored;
    }
    return 'dark'; // Always default to dark
  });

  useEffect(() => {
    const root = document.documentElement;

    // Route-driven theme system: ONLY the homepage stays dark; every other public route
    // is forced light by index.html's inline bootstrap script + AppContent's route effect.
    // ThemeContext therefore skips DOM class manipulation for non-admin routes and only
    // honors localStorage when on an admin/store/employee-portal route (which manage their
    // own theming via [data-admin-panel] anyway).
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const isManagedByThemeContext =
      path.startsWith('/admin') ||
      path.startsWith('/store') ||
      path.startsWith('/employee-portal') ||
      path.startsWith('/webdesign');

    if (isManagedByThemeContext) {
      if (theme === 'dark') {
        root.classList.add('dark');
        root.classList.remove('light');
      } else {
        root.classList.add('light');
        root.classList.remove('dark');
      }
    }

    localStorage.setItem('2a-theme', theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const stored = localStorage.getItem('2a-theme');
      if (!stored) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');
  const setSystemTheme = () => {
    localStorage.removeItem('2a-theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setTheme(prefersDark ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      toggleTheme, 
      setLightTheme, 
      setDarkTheme, 
      setSystemTheme,
      isDark: theme === 'dark'
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
